import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Sidebar } from '../components/Sidebar';
import { MetricCard } from '../components/MetricCard';
import { StatusPill } from '../components/StatusPill';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileCheck, 
  Clock, 
  AlertTriangle, 
  ScanBarcode,
  Search,
  FileUp,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const { api, isGestor } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [searchCode, setSearchCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, statsRes] = await Promise.all([
        api.get('/invoices'),
        isGestor() ? api.get('/dashboard/stats') : Promise.resolve({ data: null })
      ]);
      
      setInvoices(invoicesRes.data);
      if (statsRes.data) setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchCode.trim()) return;
    
    setSearching(true);
    try {
      const response = await api.get(`/invoices/search/${searchCode}`);
      navigate('/conference', { state: { invoice: response.data } });
    } catch (error) {
      toast.error('NF-e não encontrada');
    } finally {
      setSearching(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '0min';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}min ${secs}s` : `${secs}s`;
  };

  const formatEmissionDate = (value) => {
    if (!value) return '-';
    const [year, month, day] = String(value).split('-');
    if (year && month && day) return `${day}/${month}/${year}`;
    return value;
  };

  if (loading) {
    return (
      <div className="flex min-h-screen">
        <Sidebar />
        <main className="flex-1 md:ml-64 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#1a365d]" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-6 md:p-8 lg:p-10">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-[#1a365d]" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-1">
            Visão geral das conferências
          </p>
        </div>

        {/* Quick Search */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 mb-8">
          <h2 className="text-lg font-heading font-semibold text-slate-800 mb-4">
            Busca Rápida por Código de Barras
          </h2>
          <form onSubmit={handleSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <Input
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                placeholder="Digite ou bipe o código da NF-e..."
                className="h-14 text-xl pl-12 font-mono border-slate-300 focus:ring-2 focus:ring-[#1a365d]"
                data-testid="search-barcode-input"
              />
            </div>
            <Button 
              type="submit"
              disabled={searching}
              className="h-14 px-8 bg-[#1a365d] hover:bg-[#1a365d]/90 text-lg"
              data-testid="search-button"
            >
              {searching ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <ScanBarcode className="mr-2" size={20} />
                  Buscar
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Metrics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <MetricCard
              label="NFs Conferidas"
              value={stats.total_invoices_checked}
              icon={FileCheck}
            />
            <MetricCard
              label="Tempo Médio"
              value={formatTime(Math.round(stats.average_time_seconds))}
              icon={Clock}
            />
            <MetricCard
              label="Divergências"
              value={stats.divergence_count}
              icon={AlertTriangle}
            />
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <button
            onClick={() => navigate('/upload')}
            className="flex items-center gap-4 p-6 bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all hover:border-[#1a365d]/30"
            data-testid="upload-xml-button"
          >
            <div className="p-4 bg-[#1a365d]/10 rounded-lg">
              <FileUp size={28} className="text-[#1a365d]" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-heading font-semibold text-slate-800">
                Upload XML / Busca por nota ou chave
              </h3>
              <p className="text-slate-500 text-sm">
                Importar por XML ou consultar nota/chave para conferência
              </p>
            </div>
          </button>

          <button
            onClick={() => navigate('/conference')}
            className="flex items-center gap-4 p-6 bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-all hover:border-[#1a365d]/30"
            data-testid="start-conference-button"
          >
            <div className="p-4 bg-emerald-100 rounded-lg">
              <ScanBarcode size={28} className="text-emerald-600" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-heading font-semibold text-slate-800">
                Iniciar Conferência
              </h3>
              <p className="text-slate-500 text-sm">
                Selecionar NF-e e iniciar conferência
              </p>
            </div>
          </button>
        </div>

        {/* Recent Invoices */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
          <div className="px-6 py-4 border-b border-slate-100">
            <h2 className="text-lg font-heading font-semibold text-slate-800">
              NF-e Recentes
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Número
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Chave
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Emissão
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Itens
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Ação
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invoices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-slate-500">
                      Nenhuma NF-e cadastrada. Faça upload de um XML para começar.
                    </td>
                  </tr>
                ) : (
                  invoices.slice(0, 10).map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4 font-mono font-medium text-slate-800">
                        {invoice.invoice_number}
                      </td>
                      <td className="px-6 py-4 font-mono text-sm text-slate-600 max-w-[200px] truncate">
                        {invoice.invoice_key}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {formatEmissionDate(invoice.emission_date)}
                      </td>
                      <td className="px-6 py-4 text-slate-600">
                        {invoice.products?.length || 0}
                      </td>
                      <td className="px-6 py-4">
                        <StatusPill status={invoice.status} />
                      </td>
                      <td className="px-6 py-4 text-right">
                        {invoice.status !== 'completed' && !invoice.is_locked && (
                          <Button
                            size="sm"
                            onClick={() => navigate('/conference', { state: { invoice } })}
                            className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                            data-testid={`conference-btn-${invoice.invoice_number}`}
                          >
                            <ScanBarcode size={16} className="mr-1" />
                            Conferir
                          </Button>
                        )}
                        {invoice.is_locked && (
                          <span className="text-sm text-slate-400">Travada</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
